import { type AxiosInstance, type ResponseType } from 'axios'

interface OldEsgMetric {
  templates: Array<number>
  topics: Array<number>
  metrics: Array<string>
}

type EsgApprovalWorkflow = {
  assets: Array<number>
  categories?: Array<number>
  metrics?: Array<string>
  topics?: Array<number>
  data_owners: Array<number>
  approvers: Array<number> | []
  data_collection_frequency:
    | 'weekly'
    | 'monthly'
    | 'quarterly'
    | 'semi-annually'
    | 'annually'
    | null
  is_approver_required: boolean
  everyone_to_respond: boolean
  expected_datapoints?: number
  redirect_link?: string
}

type DownloadDocumentParams = {
  type: string
}
type IncompletePeriodsRequestParams = {
  companyId: number | string
  metricId: number | string
  assetId: number | string
  year: number | string
}

type CompanyMember = {
  id: number
  name: string
  company: string
  user: {
    id: number
    email: string
    first_name: string
    last_name: string
  }
  is_admin: boolean
}

interface ManageWorkflowData {
  asset: number
  data_owners: CompanyMember[]
  approvers: CompanyMember[]
  data_collection_frequency: string
  is_approver_required: boolean
  everyone_to_respond: boolean
  category?: number
  expected_datapoints?: number
  metric?: number | string
}

type MetricPeriodParams = IncompletePeriodsRequestParams & {
  selectedPeriod: string
}

export type AssignmentListRequestParams = {
  // Assuming all params are optional
  page?: number
  page_size?: number
  theme?: string
  user_id?: number
  categories?: string
  metrics?: string
  assets?: string
  data_owners?: string
  approvers?: string
  data_collection_frequencies?: string
  expected_datapoints?: string
  approval_types?: string
  from_datapoint?: number
  to_datapoint?: number
  sort_by_metric?: 'asc' | 'desc'
  sort_by_asset?: 'asc' | 'desc'
  sort_by_frequency?: 'asc' | 'desc'
  sort_by_approval_type?: 'asc' | 'desc'
  sort_by_approver?: 'asc' | 'desc'
  sort_by_data_owner?: 'asc' | 'desc'
  sort_by_expected_datapoint?: 'asc' | 'desc'
}

type EsgMetricFiltersQuery = {
  metrics?: string
  assets?: string
  data_collection_frequencies: string
  data_owners?: string
  approvers?: string
}

type ValidateBulkUserAssignmentImportFileRequest = {
  file: File
}

type ValidateBulkUserAssignmentImportFileResponse = {
  uploaded_file_id: string | null
  summary: {
    total_row_count: number
    error_row_count: number
  }
  errors: Array<{
    row_number: number
    error_message: string
  }>
}

type BulkImportUserAssignmentRequest = {
  file_id: string
  importMode?: 'sync' | 'async'
  notifyUser?: boolean
}

type BulkImportUserAssignmentResponse = {
  job_id: string | null
  message: string
}

export default (axios: AxiosInstance, store) => ({
  listAll(companyId: number | string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/list/`)
  },
  create(companyId: number | string, createData: OldEsgMetric) {
    return axios.post(
      `/v1/esgmetrics/company/${companyId}/create/`,
      createData,
    )
  },
  update(
    resourceId: number,
    companyId: number | string,
    updateData: OldEsgMetric,
  ) {
    return axios.put(
      `/v1/esgmetrics/company/${companyId}/retrieve-update/${resourceId}/`,
      updateData,
    )
  },
  groupedEsgMetrics(companyId: number, query: string = '') {
    return axios.get(`/v1/esgmetrics/company/${companyId}/user-selected-esg-metrics${query}`)
  },
  listFrameworks() {
    return axios.get('/v1/esgmetrics/get-frameworks/')
  },
  manageAssignment(companyId: number, selectedData: ManageWorkflowData) {
    const { origin } = useRequestURL()

    return axios.post(`/v1/esgmetrics/company/${companyId}/manage-assignment/`, {
      ...selectedData,
      redirect_link: `${origin}/dashboard/assignments`,
    })
  },
  manageAssignmentMetricAssets(companyId: number, assetId: number, metricType: number | string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/manage-assignment-metric-asset-list/?asset_id=${assetId}&${metricType}`)
  },
  assignApprovalWorkflow(
    companyId: number,
    workflowData: EsgApprovalWorkflow,
  ) {
    const payload = {
      ...workflowData,
    }
    return axios.post(
      `/v1/esgmetrics/company/${companyId}/assign-asset/`,
      payload,
    )
  },
  dataOwnerGhgAssets(companyId: number) {
    return axios.get(
      `/v1/esgmetrics/company/${companyId}/ghg-asset-assignment-list/`,
    )
  },
  approverGhgAssets(companyId: number) {
    return axios.get(
      `/v1/esgmetrics/company/${companyId}/ghg-approver-asset-assignment-list/`,
    )
  },
  uploadSupportingDocuments(companyId: number, filesData: File[], queryString: string) {
    return axios.post(
      `/v1/esgmetrics/company/${companyId}/supporting-documents-azure-upload/${queryString}`,
      filesData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: ({ loaded, total }) => {
          if (total) {
            const value = (loaded * 100) / total
            store.uploadProgress.updateProgress(value.toFixed(0))
          }
        },
      },
    )
  },
  downloadDocument(
    companyId: number | string,
    documentName: string,
    queryParams: DownloadDocumentParams,
    responseType: ResponseType = 'blob',
  ) {
    const queryString = new URLSearchParams(queryParams).toString()
    return axios.get(
      `/v1/esgmetrics/company/${companyId}/supporting-documents-azure-download/${documentName}/?${queryString}`,
      {
        responseType,
      },
    )
  },
  unLinkDocument(
    companyId: number | string,
    emissionId: string,
    documentId: string,
  ) {
    return axios.delete(
      `/v1/esgmetrics/company/${companyId}/emission/${emissionId}/supporting-document/${documentId}/unlink/`,
    )
  },
  downloadAllDocument(companyId: number | string, files: string) {
    return axios.get(
      `/v1/esgmetrics/company/${companyId}/download-all-draft-supporting-docs/?file_names=${files}`,
      {
        responseType: 'blob',
      },
    )
  },
  ghgAssetAssignmentList(companyId: number | string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/ghg-asset-assignment-list/`)
  },
  unLinkAllDocument(companyId: number | string, categoryId: number, docId: string) {
    return axios.delete(`/v1/esgmetrics/company/${companyId}/category/${categoryId}/supporting-document/${docId}/unlink-all/`)
  },
  metricCategoryAssetsList(companyId: number | string, categoryId: number, assetIds: string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/metric-category-assets-list/?category_id=${categoryId}&assets=${assetIds}`)
  },

  listAssetsByOwnersApprovers(companyId: number | string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/assets-list-by-owners-approvers/`)
  },
  getEsgMetricsByOwner(companyId: number | string, filterQueryString: string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-by-owner-list/${filterQueryString}`)
  },
  dashboardList(companyId: number | string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/dashboard-esg-metrics-list/`)
  },
  dashboardAggregatedListByYear(companyId: number | string, year: number) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/dashboard-esg-metrics-aggregated-results/?reporting_year=${year}`)
  },
  saveSelectedDashboardMetrics(companyId: number | string, metricsData: EsgDashboardSelectedMetrics) {
    return axios.post(`/v1/esgmetrics/company/${companyId}/esg-dashboard-user-metric-selections/`, metricsData)
  },
  getReportingYearList(companyId: number | string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/reporting-year-list/`)
  },
  getMetricAssetsListByOwners(companyId: number | string, metricId: number) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/metric-assets-list-by-owners/?metric_id=${metricId}`)
  },
  getReportingPeriods(companyId: number | string, metricId: number, assetId: number, reportingYearData: string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/reporting-periods/?metric_id=${metricId}&asset_id=${assetId}&reporting_year=${reportingYearData}`)
  },
  getApproversReportingYearList(companyId: number | string, metricId: number, assetId: number) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-approvers-reporting-year-list/?metric_id=${metricId}&asset_id=${assetId}`)
  },
  getMetricAssetsListByApprovers(companyId: number | string, metricId: number) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-assets-list-by-approvers/?metric_id=${metricId}`)
  },
  getApproversReportingPeriods(companyId: number | string, metricId: number, assetId: number, reportingYearData: string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-approvers-reporting-periods-list/?metric_id=${metricId}&asset_id=${assetId}&reporting_year=${reportingYearData}`)
  },
  getMetricAssetReportingData(params: IncompletePeriodsRequestParams) {
    const { companyId, metricId, assetId, year } = params
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-asset-reporting-ytd/?metric_id=${metricId}&asset_id=${assetId}&reporting_year=${year}`)
  },
  getMetricAssetPeriodData(params: MetricPeriodParams) {
    const { companyId, metricId, assetId, year, selectedPeriod } = params
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-asset-data-list/?metric_id=${metricId}&asset_id=${assetId}&reporting_year=${year}&reporting_period=${selectedPeriod}`)
  },
  getReportingYtdData(params: MetricPeriodParams, withMagicWand: boolean = false) {
    const { companyId, metricId, assetId, year } = params
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-asset-reporting-ytd/?metric_id=${metricId}&asset_id=${assetId}&reporting_year=${year}&wand_magic=${withMagicWand}`)
  },
  updateEsgMetricAssetData(companyId: number | string, assetId: number | string, actionType: string, formData: FormData) {
    const queryString = actionType && typeof actionType === 'string' ? `?action=${actionType}` : ''
    return axios.put(`/v1/esgmetrics/company/${companyId}/esg-metric-asset-data-update/${assetId}/${queryString}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  periodicDataList(companyId: number | string, metricId: string, year: number) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/data-for-excel-download-esg-dashboard/?metric_id=${metricId}&reporting_year=${year}`)
  },
  uploadEsgMetricAssetData(companyId: number | string, actionType: string, formData: FormData) {
    const queryString = actionType && typeof actionType === 'string' ? `?action=${actionType}` : ''
    return axios.post(`/v1/esgmetrics/company/${companyId}/esg-metric-asset-data-upload/${queryString}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deleteSupportingDocuments(companyId: number | string, file: File) {
    return axios.delete(`/v1/esgmetrics/company/${companyId}/supporting-documents-azure-delete/${file}/`)
  },
  convertPdfToPng(pageIndex: number, formData: FormData) {
    return axios.post(`https://baca-dev.azurewebsites.net/convert_pdf_to_png?page=${pageIndex}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob',
    })
  },
  getIncompletePeriodsPendingApprovals(params: IncompletePeriodsRequestParams) {
    const { companyId, metricId, assetId, year } = params
    const url = `esgmetrics/company/${companyId}/esg-metric-incomplete-periods-pending-approvals/?metric_id=${metricId}&asset_id=${assetId}&reporting_year=${year}`
    return axios.get(`/v1/${url}`)
  },
  esgMetricAssetList(companyId: number, params: AssignmentListRequestParams) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-asset-list/`, { params: params })
  },
  esgMetricFilters(companyId: number, params: EsgMetricFiltersQuery) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/esg-metric-filters/`, { params: params })
  },
  unLinkAllCategoryDocument(
    companyId: number | string,
    emissionId: string,
  ) {
    return axios.delete(
      `/v1/esgmetrics/company/${companyId}/emission/${emissionId}/unlink-all/`,
    )
  },
  downloadBulkUserAssignmentImportTemplate(companyId: number | string) {
    return axios.get(`/v1/esgmetrics/company/${companyId}/data-assignment/bulk-import-template/`, {
      responseType: 'blob',
    })
  },
  validateBulkUserAssignmentImportFile(companyId: number | string, requestBody: ValidateBulkUserAssignmentImportFileRequest) {
    const formData = new FormData()
    formData.append('file', requestBody.file)

    return axios.post<ValidateBulkUserAssignmentImportFileResponse>(`/v1/esgmetrics/company/${companyId}/data-assignment/validate-assignments/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  bulkImportUserAssignment(companyId: number | string, requestBody: BulkImportUserAssignmentRequest) {
    return axios.post<BulkImportUserAssignmentResponse>(`/v1/esgmetrics/company/${companyId}/data-assignment/bulk-import/`, requestBody)
  },
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  configurations(params: EsgMetricsQueryParams) {
    return axios.get<PaginatedResponseWithSummary<EsgThemeConfiguration, EsgConfigurationsCountSummary>>(
      '/v2/esg/metrics/configurations/',
      { params },
    )
  },
  userRoles() {
    return axios.get<CompanyUserRoles[]>('/v2/esg/company-user-roles/')
  },
  getTemplateById(templateId: number) {
    return axios.get<EsgDataCollectionTemplate>(
      `/v2/esg/data-collection-templates/${templateId}/`,
    )
  },
  createTemplate(payload: EsgTemplatePayload) {
    return axios.post<EsgDataCollectionTemplate>(
      '/v2/esg/data-collection-templates/',
      payload,
    )
  },
  updateTemplate(templateId: string, payload: EsgTemplatePayload) {
    return axios.patch<EsgDataCollectionTemplate>(
      `/v2/esg/data-collection-templates/${templateId}/`,
      payload,
    )
  },
  deleteTemplate(templateId: string) {
    return axios.delete(`/v2/esg/data-collection-templates/${templateId}/`)
  },
  getAll(params: EsgMetricsQueryParams) {
    return axios.get<PaginatedResponseWithSummary<EsgMetric, EsgConfigurationsCountSummary>>('/v2/esg/metrics/', { params })
  },
  getById(id: string) {
    return axios.get<EsgMetric>(`/v2/esg/metrics/${id}`)
  },
  getMetricsFilterOptions(params: EsgMetricFilterOptionsParams) {
    return axios.get<EsgMetricFilterOptions>(`/v2/esg/metrics/filter-options/`, { params })
  },
  listMetricAsset(params?: EsgMetricAssetAssignmentQueryParams) {
    return axios.get<PaginatedResponse<EsgMetricAsset>>(
      '/v2/esg/metric-asset/',
      { params },
    )
  },
  listTemplates(params?: EsgMetricTemplatesQueryParams) {
    return axios.get<PaginatedResponse<EsgTemplateWithRelations>>(
      '/v2/esg/data-collection-templates/',
      { params },
    )
  },
  getTemplatesFilterOptions() {
    return axios.get<EsgTemplateFilterOptions>(`/v2/esg/data-collection-templates/filter-options/`)
  },
  createNew(payload: CreateEsgMetricPayload) {
    return axios.post('/v2/esg/metrics/', payload)
  },
  select(payload: SelectMetricsPayload) {
    return axios.post('/v2/esg/metrics/select/', payload)
  },
  updateCustom(id: string, payload: UpdateEsgMetricPayload) {
    return axios.patch(`/v2/esg/metrics/${id}/`, payload)
  },
  updateMetricAssetAssignments(metricAssetId: string, payload: UpdateMetricAssetAssignmentPayload) {
    return axios.patch(`/v2/esg/metric-asset/${metricAssetId}/assignments/`, payload)
  },
  createMetricAsset(payload: CreateMetricAssetPayload) {
    return axios.post(`/v2/esg/metric-asset/`, payload)
  },
  exportTemplates(payload: ExportTemplatesPayload) {
    return axios.post('/v2/esg/data-collection-templates/export-data/', payload, {
      responseType: 'blob',
    })
  },
  uploadTemplateFile(templateId: number, file: File, mappedHeaders?: EsgColumnMapping[]) {
    const formData = new FormData()
    formData.append('template_id', templateId.toString())
    formData.append('file', file)
    if (mappedHeaders) {
      formData.append('headers_mapping', JSON.stringify(mappedHeaders))
    }

    return axios.post('/v2/esg/submissions/templates/data/upload-by-file/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  getSubmissionRawData(params: EsgSubmissionRawDataParams) {
    return axios.get<PaginatedResponse<EsgSubmissionTemplateData>>('/v2/esg/submissions/templates/data', { params })
  },
  getMetricRawData(metricId: string, params: EsgMetricRawDataParams) {
    return axios.get<PaginatedResponse<EsgMetricRawData>>(`/v2/esg/metrics/${metricId}/raw_data/`, { params })
  },
  // Data Submissions
  listSubmissions(params?: ListEsgSubmissionsQueryParams) {
    const queryParams = params?.status
      ? { ...params, status: params.status.join(',') }
      : params

    return axios.get<PaginatedResponseWithSummary<EsgSubmission, EsgSubmissionResponseSummary>>(
      '/v2/esg/submissions/',
      { params: queryParams },
    )
  },
  submissionById(submissionId: number) {
    return axios.get<EsgSubmission>(`/v2/esg/submissions/${submissionId}/`)
  },
  listSubmissionsFilterOptions(params: EsgSubmissionsFilterOptionsParams) {
    return axios.get<EsgSubmissionsFilterOptions>(`/v2/esg/submissions/filter-options/`, { params })
  },
  getUserSubmissionTemplates(params?: SubmissionTemplatesQueryParams) {
    return axios.get('/v2/esg/submissions/templates/', { params })
  },
  deleteRawSubmission(id: number) {
    return axios.delete(`v2/esg/submissions/templates/data/${id}/`)
  },
  addRawSubmission(payload: NewSubmissionApiPayload) {
    return axios.post('/v2/esg/submissions/templates/data/', payload)
  },
  uploadMultipleSupportingDocuments(formData: FormData) {
    return axios.post('v2/esg/submissions/templates/data/supporting-document/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  downloadSupportingDocument(documentId: string) {
    return axios.get(`/v2/esg/submissions/files/${documentId}/download/`, {
      responseType: 'blob',
    })
  },
  deleteSupportingDocument(documentId: string) {
    return axios.delete(`/v2/esg/submissions/files/${documentId}/`)
  },
  reviewSubmission(submissionId: string, payload: ReviewSubmissionPayload) {
    return axios.post(`/v2/esg/submissions/${submissionId}/review/`, payload)
  },
  getSubmissionComments(submissionId: string) {
    return axios.get<EsgSubmissionCommentsResponse>(`/v2/esg/submissions/${submissionId}/comments/`)
  },
  updateSubmissionComment(submissionId: string, commentId: string, payload: UpdateSubmissionCommentPayload) {
    return axios.patch(`/v2/esg/submissions/${submissionId}/comments/${commentId}/`, payload)
  },
  addSubmissionComment(payload: AddSubmissionCommentPayload) {
    const submissionId = payload.submission_id
    return axios.post(`/v2/esg/submissions/${submissionId}/comments/`, payload)
  },
  addSubmissionDataRow(payload: AddRowPayload) {
    return axios.post('/v2/esg/submissions/templates/data/', payload)
  },
  deleteSubmissionDataRow(rawDataId: string) {
    return axios.delete(`/v2/esg/submissions/templates/data/${rawDataId}`)
  },
  submitSubmission(payload: SubmitSubmissionPayload) {
    return axios.post('/v2/esg/submissions/submit/', payload)
  },
  createManualTemplateData(payload: EsgManualEntryPayload) {
    const url = '/v2/esg/submissions/templates/data/manual-entry/'

    return axios.post(url, payload)
  },
  getTemplateAssignedAssets(templateId: string | number) {
    return axios.get(`/v2/esg/data-collection-templates/${templateId}/assigned-assets/`)
  },
  getSubmissionPermissionsList(submissionId: number) {
    return axios.get<EsgSubmissionPermissionsList>(`/v2/esg/submissions/${submissionId}/permissions/`)
  },
  reportingPeriods() {
    return axios.get<EsgMetricReportingPeriod[]>('/v2/esg/metrics/reporting-periods/')
  },
  metricTableOutput(metricId: number, params?: DateRangeFilters) {
    return axios.get<HandsontableMetadataOutput>(`/v2/esg/metrics/${metricId}/output/`, { params })
  },
  executeQuery(payload: ExecuteQueryPayload) {
    return axios.post<ExecuteQueryResponse>('/v2/esg/admin/data-transformation/query/execute/', payload)
  },
  saveQuery(payload: SaveQueryPayload) {
    return axios.post('/v2/esg/admin/data-transformation/query/save/', payload)
  },
})
